import {useState, useEffect, useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {CheckCircle} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import {LinkedInButton} from '../../../../../components/LinkedInButton/LinkedInButton';
import {UserContext} from '../../../../../contexts/UserContext/UserContext';
import {getLogo} from '../../../../../layouts/theme/Utils';
import {AppConfig} from '../../../../../lib/AppConfig';
import {OAUTH_PROVIDER_LINKEDIN} from '../../../../../lib/constants';
import CookieService from '../../../../../services/CookieService';
import {OAuthService} from '../../../../../services/oauth-service';
import {TalentService} from '../../../../talent/services/TalentService';
import {ApplicantOnboardingService} from '../../../onboarding/services/ApplicantOnboardingService';

interface Props {
  isDisabled?: boolean;
  talentSurveyId?: string;
}

const isLocalStorageAvailable = () => {
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    return true;
  } catch (e) {
    return false;
  }
};

export default function LinkedInSignup({isDisabled, talentSurveyId}: Props) {
  const {t} = useTranslation();
  const {userContextLogin} = useContext(UserContext);
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;

  const [isBackDropOpen, setIsBackDropOpen] = useState(false);
  const [isLinkedInIntegrationInProgress, setIsLinkedInIntegrationInProgress] = useState(false);
  const [isStepOneCompleted, setIsStepOneCompleted] = useState(false);
  const [isStepTwoCompleted, setIsStepTwoCompleted] = useState(false);
  const [isAuthorizationComplete, setIsAuthorizationComplete] = useState(false);
  const [stepError, setStepError] = useState(0);
  const handleCloseBackdrop = () => {
    setIsLinkedInIntegrationInProgress(false);
    setIsBackDropOpen(false);
  };

  useEffect(() => {
    if (isLinkedInIntegrationInProgress && isLocalStorageAvailable()) {
      let authorizationCode = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
      let linkedinState = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);
      let errorMessage = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);

      const authorizationCheck = setInterval(() => {
        authorizationCode = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
        linkedinState = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);
        errorMessage = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);

        if (errorMessage !== null) {
          setStepError(1);

          setTimeout(() => {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);
            clearInterval(authorizationCheck);
            handleCloseBackdrop();
          }, 5000);
        }

        if (authorizationCode && linkedinState) {
          clearInterval(authorizationCheck);
          setIsAuthorizationComplete(true);
          setIsStepOneCompleted(true);
        }
      }, 2000);

      if (isAuthorizationComplete && authorizationCode && linkedinState) {
        OAuthService.signupCallback(OAUTH_PROVIDER_LINKEDIN, authorizationCode, linkedinState).then((res) => {
          if (res.ok) {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);

            setIsStepTwoCompleted(true);
            setIsLinkedInIntegrationInProgress(false);
            setIsBackDropOpen(false);
            const user = res.data.user;

            const applicantRoleData = {
              avatarUrl: user.role.data.avatar_url,
              applicantId: user.role.data.applicant_id,
              applicantOnboardingId: user.role.data.applicant_onboarding_id,
              onboardingCompleted: user.role.data.onboarding_completed,
              talentSurveyId: user.role.data.talent_survey_id,
            };

            const signedUpUser = {
              id: user.resource_id,
              applicantId: user.role.data.applicant_id,
              applicantOnboardingId: user.role.data.applicant_onboarding_id,
              email: user.email,
              firstName: user.first_name,
              lastName: user.last_name,
              role: {
                type: user.role.type,
                data: applicantRoleData,
              },
              accountConfirmed: user.account_confirmed,
              token: user.meta.auth_token,
              refreshToken: user.meta.refresh_token,
            };

            CookieService.setCookie(signedUpUser);

            if (currentPath.includes(AppConfig.SIGN_UP_PATH)) {
              TalentService.attributeTalentSurvey(talentSurveyId || '', applicantRoleData.applicantId);
            }

            localStorage.setItem(
              AppConfig.LOCAL_STORAGE_LINKEDIN_PUBLIC_PROFILE_URL,
              user.social_account_integration_data.profile_url as string
            );

            localStorage.setItem(
              AppConfig.LOCAL_STORAGE_LINKEDIN_PUBLIC_PICTURE_URL,
              user.social_account_integration_data.profile_picture_url as string
            );

            ApplicantOnboardingService.start(applicantRoleData.applicantId).then((applicantOnboarding) => {
              if (applicantOnboarding) {
                applicantRoleData.applicantOnboardingId = applicantOnboarding.data?.id;
                user.role.data = applicantRoleData;
                user.role.data.onboardingCompleted = !!applicantOnboarding.data?.completedAt;
              }

              userContextLogin(signedUpUser);

              if (signedUpUser?.role?.data?.onboardingCompleted) {
                navigate(AppConfig.DASHBOARD_PATH);
              } else {
                navigate(AppConfig.APPLICANT_ONBOARDING_PATH);
              }
            });
          } else {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);

            setStepError(2);

            setTimeout(() => {
              handleCloseBackdrop();
            }, 10000);
          }
        });
      }

      return () => {
        clearInterval(authorizationCheck);
      };
    }
  }, [
    isAuthorizationComplete,
    isLinkedInIntegrationInProgress,
    navigate,
    userContextLogin,
    currentPath,
    talentSurveyId,
  ]);

  const handleButtonClick = () => {
    setIsLinkedInIntegrationInProgress(true);
    setIsBackDropOpen(true);

    OAuthService.authorizeSignup(OAUTH_PROVIDER_LINKEDIN).then((res) => {
      if (res.ok) {
        setTimeout(() => {
          window.open(res.data?.providerAuthorizationUrl, '_blank');
        });
      } else {
        setStepError(1);

        setTimeout(() => {
          setIsLinkedInIntegrationInProgress(false);
          setIsBackDropOpen(false);
        }, 5000);
      }
    });
  };
  return (
    <Box display='flex' justifyContent={'center'} width={'100%'}>
      <LinkedInButton
        onClick={handleButtonClick}
        isDisabled={isDisabled}
        hoverBackgroundColor={'#4486C9'}
        hoverTextColor={'white'}
      />
      <Dialog
        open={isBackDropOpen}
        onClose={handleCloseBackdrop}
        sx={{
          '& .MuiDialog-paper': {
            backgroundImage: 'none',
          },
          p: 3,
        }}
      >
        <Box display={'flex'} justifyContent={'flex-end'} p={2}>
          <IconButton onClick={handleCloseBackdrop}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          display={'flex'}
          sx={{
            justifyContent: {xs: 'center', md: 'center'},
            p: 3,
          }}
        >
          <Box component={'img'} src={getLogo()} alt='Empion Logo' />
        </Box>
        <DialogTitle textAlign={'center'}>{t('applicantOnboarding.linkedinConnection.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography component='span' variant='h4' textAlign={'center'}>
              {t('applicantOnboarding.linkedinConnection.dialog.content')}
            </Typography>
          </DialogContentText>
          <>
            <Divider sx={{my: 2}} />
            {stepError > 0 && (
              <Typography component={'span'} variant='h5' color={'error'} textAlign={'center'}>
                {t('applicantOnboarding.linkedinConnection.dialog.error')}
              </Typography>
            )}
            <List dense={true}>
              <ListItem component={'span'}>
                <ListItemIcon>
                  {isStepOneCompleted ? <CheckCircle /> : stepError === 1 ? <ErrorIcon /> : <CircularProgress />}
                </ListItemIcon>
                <ListItemText primary={t('applicantOnboarding.linkedinConnection.dialog.authorization')} />
              </ListItem>
              <ListItem component={'span'}>
                <ListItemIcon>
                  {isStepTwoCompleted ? <CheckCircle /> : stepError === 2 ? <ErrorIcon /> : <CircularProgress />}
                </ListItemIcon>
                <ListItemText primary={t('applicantOnboarding.linkedinConnection.dialog.connection')} />
              </ListItem>
            </List>
          </>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
